/**
 * What has been uploaded, so the page can still show it tomorrow.
 *
 * Allen, 2026-08-29: "make it show all the files that have been uploaded, in
 * some kind of history view, and also save the name and show it for all of
 * it." Before this, "Recent captures" was built by `ui.addCapture` from
 * uploads made in the open tab, and a reload said "Nothing uploaded yet"
 * however many recordings had gone up.
 *
 * **The bucket is not the history.** The obvious implementation — ask S3
 * what is under your prefix — is wrong in a way that hides: the desktop
 * deletes each object once its transcript is filed, so that list shows only
 * the recordings that have *not* been transcribed. It empties as the system
 * works and fills as it breaks.
 *
 * The same shape as sessions.js and canvas.js: the page sends the Cognito ID
 * token it already holds, and nothing here touches the DOM, so the rules are
 * testable without a browser.
 *
 * One failure mode, not six. A history that cannot be reached is a history
 * that cannot be shown, and the page has exactly one thing to say about it.
 */

export class MemosUnavailable extends Error {
  constructor(message) {
    super(message || 'Could not reach your uploads.');
    this.name = 'MemosUnavailable';
  }
}

async function call(config, session, path, { method = 'GET', body } = {}, fetchImpl = fetch) {
  if (!config?.sessionsApiUrl) {
    // Checked before the network, so a site deployed without the API says so
    // instead of spending a round trip to find out.
    throw new MemosUnavailable('Uploads are not set up for this site yet.');
  }

  const init = {
    method,
    headers: { authorization: `Bearer ${session?.idToken}` },
  };
  if (body !== undefined) {
    init.headers['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetchImpl(`${config.sessionsApiUrl}${path}`, init);
  } catch {
    throw new MemosUnavailable('No connection.');
  }

  if (response.status === 401 || response.status === 403) {
    throw new MemosUnavailable('Your sign-in has expired. Sign in again.');
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) throw new MemosUnavailable(payload?.message);
  return payload;
}

/**
 * Write down that a recording went up.
 *
 * Called after the transfer resolves, never before, so the history never
 * lists a file that did not arrive.
 *
 * `name` is the file's own name, not the sanitised one in the S3 key. The key
 * holds "1787867641800-Lecture_3_final.m4a", and recovering "Lecture 3
 * (final).m4a" from that is guesswork — which is the half of the ask that
 * would otherwise be quietly lost.
 */
export async function recordMemo(config, session, { key, file, course }, fetchImpl) {
  const body = await call(config, session, '/memos', {
    method: 'POST',
    body: {
      key,
      name: file?.name ?? '',
      course: course?.meta || course?.name || '',
      courseName: course?.name || '',
      size: file?.size ?? 0,
      uploadedAt: Date.now() / 1000,
    },
  }, fetchImpl);
  return body?.memo ?? null;
}

/** Everything uploaded on this account, newest first. */
export async function listMemos(config, session, since = 0, fetchImpl) {
  const body = await call(config, session,
    `/memos?since=${encodeURIComponent(since)}`, {}, fetchImpl);
  return body?.memos ?? [];
}
